import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Row, Col, Card, Statistic, Table, Tag, Space, Typography,
  Spin, Alert, Tooltip, Button, Empty, Switch
} from 'antd';
import {
  ClockCircleOutlined, DollarOutlined, EyeOutlined, UserOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { contractService, clientVendorService } from '../services/api';
import { usePermissions } from '../contexts/PermissionContext';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const RENEWAL_STAGES = [
  { days: 120, stage: 'review',       label: 'Review',       color: 'blue',    hex: '#1890ff', range: '91-120 days', shortAction: 'Review usage & need',  action: 'Review usage vs entitlements. Do you still need this service?' },
  { days: 90,  stage: 'decision',     label: 'Decision',     color: 'orange',  hex: '#fa8c16', range: '61-90 days',  shortAction: 'Renew or terminate',   action: 'Deadline for Renew vs Terminate decision.' },
  { days: 60,  stage: 'negotiation',  label: 'Negotiation',  color: 'volcano', hex: '#fa541c', range: '31-60 days',  shortAction: 'Negotiate terms',      action: 'Negotiations on price/terms should begin. Start legal review.' },
  { days: 30,  stage: 'finalization', label: 'Finalization', color: 'red',     hex: '#f5222d', range: '0-30 days',   shortAction: 'Finalize & sign',      action: 'Contract should be ready for signature. Escalation if not finalized.' },
];

const getStageForDays = (daysRemaining) => {
  if (daysRemaining <= 30) return RENEWAL_STAGES.find(s => s.stage === 'finalization');
  if (daysRemaining <= 60) return RENEWAL_STAGES.find(s => s.stage === 'negotiation');
  if (daysRemaining <= 90) return RENEWAL_STAGES.find(s => s.stage === 'decision');
  return RENEWAL_STAGES.find(s => s.stage === 'review');
};

const getDaysColor = (days) => {
  if (days <= 30) return '#f5222d';
  if (days <= 60) return '#fa541c';
  if (days <= 90) return '#fa8c16';
  return '#1890ff';
};

const Renewals = () => {
  const navigate = useNavigate();
  const tableRef = useRef(null);
  const { user } = usePermissions();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [activeFilter, setActiveFilter] = useState(null);
  const [showMyOnly, setShowMyOnly] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [contractsData, vendorsData] = await Promise.all([
          contractService.getAll({ expiring_within_days: 120, status: 'active', limit: 200 }),
          clientVendorService.getAll(),
        ]);
        setContracts(contractsData);
        setVendors(vendorsData);
        setError(null);
      } catch (err) {
        console.error('Error fetching renewal pipeline data:', err);
        setError('Failed to load renewal pipeline. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getVendorName = (record) => {
    const vendor = vendors.find(v => v.id === record.client_vendor_id);
    return vendor?.vendor?.name || '-';
  };

  const getOwnerName = (record) => {
    const vendor = vendors.find(v => v.id === record.client_vendor_id);
    return vendor?.business_owner?.full_name || null;
  };

  const getOwnerId = (record) => {
    const vendor = vendors.find(v => v.id === record.client_vendor_id);
    return vendor?.business_owner?.id || null;
  };

  // Classify contracts into stages, applying "My Contracts" filter
  const classifiedContracts = useMemo(() => {
    return contracts
      .map(c => {
        if (!c.end_date) return null;
        const daysRemaining = dayjs(c.end_date).diff(dayjs(), 'day');
        if (daysRemaining < 0) return null;
        const stage = getStageForDays(daysRemaining);
        return { ...c, daysRemaining, renewalStage: stage };
      })
      .filter(Boolean)
      .filter(c => !showMyOnly || getOwnerId(c) === user?.id);
  }, [contracts, showMyOnly, user, vendors]);

  // Group by stage
  const stageBuckets = useMemo(() => {
    const buckets = {};
    RENEWAL_STAGES.forEach(s => {
      buckets[s.stage] = { stage: s, contracts: [], totalValue: 0 };
    });
    classifiedContracts.forEach(c => {
      const key = c.renewalStage.stage;
      buckets[key].contracts.push(c);
      buckets[key].totalValue += c.total_value || 0;
    });
    return buckets;
  }, [classifiedContracts]);

  // Filtered data for table
  const tableData = useMemo(() => {
    if (!activeFilter) return classifiedContracts;
    return classifiedContracts.filter(c => c.renewalStage.stage === activeFilter);
  }, [classifiedContracts, activeFilter]);

  const tableColumns = [
    {
      title: 'Stage',
      key: 'stage',
      width: 120,
      render: (_, record) => (
        <Tag color={record.renewalStage.color}>{record.renewalStage.label}</Tag>
      ),
      filters: RENEWAL_STAGES.map(s => ({ text: s.label, value: s.stage })),
      onFilter: (value, record) => record.renewalStage.stage === value,
    },
    {
      title: 'Contract',
      dataIndex: 'title',
      key: 'title',
      render: (title) => <Text strong>{title}</Text>,
    },
    {
      title: 'Vendor',
      key: 'vendor',
      render: (_, record) => getVendorName(record),
    },
    {
      title: 'Days Remaining',
      key: 'daysRemaining',
      dataIndex: 'daysRemaining',
      sorter: (a, b) => a.daysRemaining - b.daysRemaining,
      defaultSortOrder: 'ascend',
      render: (days) => (
        <Text strong style={{ color: getDaysColor(days) }}>{days} days</Text>
      ),
    },
    {
      title: 'End Date',
      dataIndex: 'end_date',
      key: 'end_date',
      render: (date) => date ? dayjs(date).format('MMM D, YYYY') : '-',
    },
    {
      title: 'Value',
      dataIndex: 'total_value',
      key: 'total_value',
      render: (value, record) => value
        ? `$${value.toLocaleString()} ${record.currency || 'USD'}`
        : '-',
      sorter: (a, b) => (a.total_value || 0) - (b.total_value || 0),
    },
    {
      title: 'Auto-Renew',
      dataIndex: 'auto_renew',
      key: 'auto_renew',
      width: 100,
      render: (autoRenew) => autoRenew
        ? <Tag color="green">Yes</Tag>
        : <Tag color="default">No</Tag>,
      filters: [
        { text: 'Yes', value: true },
        { text: 'No', value: false },
      ],
      onFilter: (value, record) => record.auto_renew === value,
    },
    {
      title: 'Owner',
      key: 'owner',
      width: 150,
      render: (_, record) => {
        const owner = getOwnerName(record);
        return owner ? (
          <Text><UserOutlined style={{ marginRight: 4 }} />{owner}</Text>
        ) : (
          <Text type="secondary">Unassigned</Text>
        );
      },
    },
    {
      title: 'Action',
      key: 'action',
      width: 160,
      render: (_, record) => (
        <Tooltip title={record.renewalStage.action}>
          <Tag color={record.renewalStage.color}>{record.renewalStage.shortAction}</Tag>
        </Tooltip>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => navigate(`/contracts?id=${record.id}`)}
        >
          Details
        </Button>
      ),
    },
  ];

  const handleCardClick = (stageKey) => {
    setActiveFilter(activeFilter === stageKey ? null : stageKey);
    setTimeout(() => {
      tableRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <Title level={2} style={{ marginBottom: 4 }}>
            <ClockCircleOutlined style={{ marginRight: 8 }} />
            Renewal Pipeline
          </Title>
          <Text type="secondary">
            Track upcoming contract renewals across the 120-90-60-30 protocol stages.
          </Text>
        </div>
        <Space>
          <Text type="secondary">My Contracts</Text>
          <Switch checked={showMyOnly} onChange={setShowMyOnly} />
        </Space>
      </div>

      {error && (
        <Alert message="Error" description={error} type="error" showIcon style={{ marginTop: 16 }} />
      )}

      <Spin spinning={loading}>
        {/* Empty state */}
        {!loading && !error && classifiedContracts.length === 0 && (
          <Card style={{ marginTop: 24, textAlign: 'center', padding: 48 }}>
            <Empty
              description={
                <span>
                  No contracts expiring within 120 days.
                  <br />
                  <Text type="secondary">All active contracts are more than 120 days from expiry.</Text>
                </span>
              }
            >
              <Button type="primary" onClick={() => navigate('/contracts')}>
                View All Contracts
              </Button>
            </Empty>
          </Card>
        )}

        {classifiedContracts.length > 0 && (
          <>
            {/* Section 1: Summary Stats */}
            <Row gutter={16} style={{ marginTop: 20 }}>
              {RENEWAL_STAGES.map(stage => {
                const bucket = stageBuckets[stage.stage];
                const isActive = activeFilter === stage.stage;
                return (
                  <Col xs={24} sm={12} lg={6} key={stage.stage}>
                    <Card
                      hoverable
                      style={{
                        cursor: 'pointer',
                        marginBottom: 16,
                        borderColor: isActive ? stage.hex : undefined,
                        borderWidth: isActive ? 2 : 1,
                      }}
                      onClick={() => handleCardClick(stage.stage)}
                    >
                      <Statistic
                        title={<span>{stage.label} <Text type="secondary" style={{ fontSize: 12, fontWeight: 'normal' }}>({stage.range})</Text></span>}
                        value={bucket.contracts.length}
                        suffix={bucket.contracts.length === 1 ? 'contract' : 'contracts'}
                        valueStyle={{ color: stage.hex }}
                      />
                      <div style={{ marginTop: 8 }}>
                        <Text type="secondary" style={{ fontSize: 13 }}>
                          <DollarOutlined /> {bucket.totalValue.toLocaleString()} at risk
                        </Text>
                      </div>
                    </Card>
                  </Col>
                );
              })}
            </Row>

            {/* Section 2: Pipeline Kanban */}
            <Card title="Renewal Pipeline" style={{ marginTop: 8 }}>
              <Row gutter={16}>
                {RENEWAL_STAGES.map(stage => {
                  const bucket = stageBuckets[stage.stage];
                  return (
                    <Col xs={24} sm={12} lg={6} key={stage.stage}>
                      <div style={{
                        background: '#fafafa',
                        borderRadius: 8,
                        padding: 12,
                        minHeight: 200,
                      }}>
                        <div style={{ marginBottom: 12, textAlign: 'center' }}>
                          <Tag color={stage.color} style={{ fontSize: 14, padding: '2px 12px' }}>
                            {stage.label}
                          </Tag>
                          <div style={{ marginTop: 4 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {bucket.contracts.length} contract{bucket.contracts.length !== 1 ? 's' : ''}
                            </Text>
                          </div>
                        </div>

                        {bucket.contracts.length === 0 ? (
                          <Empty description="No contracts" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        ) : (
                          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                            {bucket.contracts.map(contract => (
                              <Card
                                key={contract.id}
                                size="small"
                                hoverable
                                style={{ marginBottom: 8, cursor: 'pointer' }}
                                onClick={() => navigate(`/contracts?id=${contract.id}`)}
                              >
                                <Text strong ellipsis style={{ display: 'block', marginBottom: 4 }}>
                                  {contract.title}
                                </Text>
                                <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                                  {getVendorName(contract)}
                                </Text>
                                {getOwnerName(contract) && (
                                  <Text style={{ fontSize: 11, display: 'block', marginTop: 2, color: '#8c8c8c' }}>
                                    <UserOutlined style={{ marginRight: 4 }} />{getOwnerName(contract)}
                                  </Text>
                                )}
                                <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Tag color={stage.color}>{contract.daysRemaining}d</Tag>
                                  {contract.total_value && (
                                    <Text style={{ fontSize: 12 }}>
                                      ${contract.total_value.toLocaleString()}
                                    </Text>
                                  )}
                                </div>
                                {contract.auto_renew && (
                                  <Tag color="green" style={{ marginTop: 4, fontSize: 11 }}>Auto-Renew</Tag>
                                )}
                              </Card>
                            ))}
                          </div>
                        )}
                      </div>
                    </Col>
                  );
                })}
              </Row>
            </Card>

            {/* Section 3: Detailed Table */}
            <div ref={tableRef}>
              <Card
                title={
                  <Space>
                    <span>Detailed View</span>
                    {activeFilter && (
                      <Tag
                        color={RENEWAL_STAGES.find(s => s.stage === activeFilter)?.color}
                        closable
                        onClose={() => setActiveFilter(null)}
                      >
                        {RENEWAL_STAGES.find(s => s.stage === activeFilter)?.label}
                      </Tag>
                    )}
                  </Space>
                }
                style={{ marginTop: 24 }}
              >
                <Table
                  columns={tableColumns}
                  dataSource={tableData}
                  rowKey="id"
                  loading={loading}
                  pagination={{
                    pageSize: 20,
                    showSizeChanger: true,
                    showTotal: (total) => `${total} contracts`,
                  }}
                  scroll={{ x: 1200 }}
                />
              </Card>
            </div>
          </>
        )}
      </Spin>
    </div>
  );
};

export default Renewals;
